import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { isAxiosError } from "axios"

import {
  listReportDatasourceViews,
  mergeSystemReportFields,
  previewReportDatasource,
  type PreviewReportDatasourceResult,
  type ReportDatasourceViewOptionDto,
  getReportTemplate,
  type ReportTemplateDetailDto,
  type ReportTemplateFieldDto,
  type ReportTemplateFilterDto,
  type ReportTemplateLayoutDto,
  type ReportLayoutWidgetDto,
} from "../api/reportTemplateService"
import {
  DEFAULT_MANUAL_PAGE_COUNT,
  DEFAULT_FLOW_SECTION_ID,
  DEFAULT_TENANT_ID,
  MIN_FLOW_SECTION_HEIGHT,
  TABLE_ROW_NO_KEY,
  WORKER_TABLE_COLUMNS,
  clamp,
  createDefaultLayout,
  createWidgetId,
  getCanvasMetrics,
  getLayoutDatasources,
  getWidgetBorderConfig,
  getWidgetCornerRadius,
  getWidgetFlowSectionId,
  getWidgetManualPage,
  getWidgetTextAlign,
  getWidgetTypographyConfig,
  getWidgetVerticalAlign,
  parseTableColumnWidthMap,
  getLayoutManualPageCount,
  getLayoutFlowSections,
  normalizeLayoutForEditor,
  normalizeLayoutFrame,
  reindexFields,
  reindexFilters,
  toFiniteNumber,
  type FontStyleMode,
  type FontWeightMode,
  type TableColumnWidthMap,
  type TableSourceMode,
} from "./dynamic-report-editor/editorUtils"
import { buildReportDatasourceContext } from "./dynamic-report-editor/reportDatasourceRuntime"
import { FieldDialog } from "./dynamic-report-editor/FieldDialog"
import { FilterDialog } from "./dynamic-report-editor/FilterDialog"
import { EditorHeaderCard } from "./dynamic-report-editor/EditorHeaderCard"
import { EditorErrorAlert, EditorLoadingCard, EditorNotFoundCard, FloatingSaveButton } from "./dynamic-report-editor/EditorStateBlocks"
import { type DesignFlowSectionBox, type LayoutSectionKey } from "./dynamic-report-editor/layoutTypes"
import { validateReportDesigner } from "./dynamic-report-editor/reportDesignerValidation"
import { ReportTemplateDefinitionTabs } from "./dynamic-report-editor/ReportTemplateDefinitionTabs"
import { TemplateSummaryCard } from "./dynamic-report-editor/TemplateSummaryCard"
import { useLayoutPreviewComposition } from "./dynamic-report-editor/useLayoutPreviewComposition"
import { useLayoutWidgetInteractions } from "./dynamic-report-editor/useLayoutWidgetInteractions"
import { useLayoutSectionManagement } from "./dynamic-report-editor/useLayoutSectionManagement"
import { useLayoutWidgetOperations } from "./dynamic-report-editor/useLayoutWidgetOperations"
import { useReportTemplateEditorActions } from "./dynamic-report-editor/useReportTemplateEditorActions"
import { useReportTemplateFieldFilterConfig } from "./dynamic-report-editor/useReportTemplateFieldFilterConfig"
import { useReportTemplatePreviewRuntime } from "./dynamic-report-editor/useReportTemplatePreviewRuntime"

interface DynamicReportTemplateEditorProps {
  reportTemplateId?: string | null
  tenantId?: string | null
  onBack?: () => void
  onRun?: (reportTemplateId: string, tenantId: string) => void
}

const formatPreviewValue = (value: unknown): string => {
  if (value == null) return "-"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  if (typeof value === "object") return JSON.stringify(value)
  const text = String(value)
  return text.trim() ? text : "-"
}

export default function DynamicReportTemplateEditor({
  reportTemplateId,
  tenantId,
  onBack,
  onRun,
}: DynamicReportTemplateEditorProps) {
  const [template, setTemplate] = useState<ReportTemplateDetailDto | null>(null)
  const [fields, setFields] = useState<ReportTemplateFieldDto[]>([])
  const [filters, setFilters] = useState<ReportTemplateFilterDto[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const [layout, setLayout] = useState<ReportTemplateLayoutDto>(createDefaultLayout())
  const [layoutViewMode, setLayoutViewMode] = useState<"design" | "preview">("design")
  const [layoutSection, setLayoutSection] = useState<LayoutSectionKey>("body")
  const [activeBodyFlowSectionId, setActiveBodyFlowSectionId] = useState(DEFAULT_FLOW_SECTION_ID)
  const [designBodyPage, setDesignBodyPage] = useState(1)
  const [snapToGrid, setSnapToGrid] = useState(true)
  const [gridSize, setGridSize] = useState(8)
  const [previewPageIndex, setPreviewPageIndex] = useState(0)
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null)
  const [datasourceViewOptions, setDatasourceViewOptions] = useState<ReportDatasourceViewOptionDto[]>([])
  const [isRefreshingDatasourceViews, setIsRefreshingDatasourceViews] = useState(false)
  const [loadingDatasourceId, setLoadingDatasourceId] = useState<string | null>(null)
  const [datasourcePreviewResults, setDatasourcePreviewResults] = useState<Record<string, PreviewReportDatasourceResult>>({})
  const canvasRef = useRef<HTMLDivElement | null>(null)
  const imageFileInputRef = useRef<HTMLInputElement | null>(null)
  const activeTenantId = template?.tenantId || tenantId || DEFAULT_TENANT_ID

  const loadTemplate = useCallback(async () => {
    if (!reportTemplateId) {
      setTemplate(null)
      setFields([])
      setFilters([])
      setLayout(createDefaultLayout())
      setLayoutSection("body")
      setDesignBodyPage(1)
      setPreviewPageIndex(0)
      setSelectedWidgetId(null)
      setError(null)
      return
    }

    setIsLoading(true)
    setError(null)
    try {
      const detail = await getReportTemplate(reportTemplateId, tenantId || DEFAULT_TENANT_ID)
      setTemplate(detail)
      setFields(reindexFields([...(detail.fields ?? [])].sort((a, b) => a.orderIndex - b.orderIndex)))
      setFilters(reindexFilters([...(detail.filters ?? [])].sort((a, b) => a.orderIndex - b.orderIndex)))
      setLayout(normalizeLayoutForEditor(detail.layoutJson))
      setLayoutSection("body")
      setActiveBodyFlowSectionId(DEFAULT_FLOW_SECTION_ID)
      setDesignBodyPage(1)
      setPreviewPageIndex(0)
      setSelectedWidgetId(null)
    } catch (err) {
      console.error(err)
      if (isAxiosError(err)) {
        const message = (err.response?.data as { message?: string } | undefined)?.message
        setError(message || "Failed to load report template detail")
      } else if (err instanceof Error) {
        setError(err.message)
      } else {
        setError("Failed to load report template detail")
      }
    } finally {
      setIsLoading(false)
    }
  }, [reportTemplateId, tenantId])

  useEffect(() => {
    loadTemplate().catch(() => undefined)
  }, [loadTemplate])

  const loadDatasourceViews = useCallback(async () => {
    setIsRefreshingDatasourceViews(true)
    try {
      const options = await listReportDatasourceViews(activeTenantId)
      setDatasourceViewOptions(options)
    } catch (viewError) {
      console.error(viewError)
    } finally {
      setIsRefreshingDatasourceViews(false)
    }
  }, [activeTenantId])

  useEffect(() => {
    loadDatasourceViews().catch(() => undefined)
  }, [loadDatasourceViews])

  useEffect(() => {
    setLayout((prev) => {
      const seen = new Set<string>()
      let changed = false
      const ensureIds = (items: ReportLayoutWidgetDto[]) =>
        items.map((widget) => {
          let nextId = String(widget.id || "").trim()
          if (!nextId || seen.has(nextId)) {
            nextId = createWidgetId()
            changed = true
          }
          seen.add(nextId)
          return nextId === widget.id ? widget : { ...widget, id: nextId }
        })

      const nextBody = ensureIds(prev.widgets ?? [])
      const nextHeader = ensureIds(prev.headerSection?.widgets ?? [])
      const nextFooter = ensureIds(prev.footerSection?.widgets ?? [])

      if (!changed) return prev
      return {
        ...prev,
        widgets: nextBody,
        headerSection: {
          ...(prev.headerSection ?? { enabled: false, height: 120, widgets: [] }),
          widgets: nextHeader,
        },
        footerSection: {
          ...(prev.footerSection ?? { enabled: false, height: 80, widgets: [] }),
          widgets: nextFooter,
        },
      }
    })
  }, [layout])

  const fieldKeyOptions = useMemo(() => {
    return mergeSystemReportFields(fields)
      .map((item) => item.fieldKey)
      .filter((item) => item.trim().length > 0)
      .sort((a, b) => a.localeCompare(b))
  }, [fields])

  const availableFields = useMemo(
    () => mergeSystemReportFields(fields),
    [fields],
  )
  const fieldOptions = useMemo(
    () =>
      availableFields
        .map((item) => ({
          key: item.fieldKey,
          label: item.label || item.fieldKey,
        }))
        .sort((left, right) => {
          if (left.key === "permit_number") return -1
          if (right.key === "permit_number") return 1
          return `${left.label} ${left.key}`.localeCompare(`${right.label} ${right.key}`)
        }),
    [availableFields],
  )
  const datasourceContextKeyOptions = useMemo(() => {
    const options = [
      { key: "permit_id", label: "Permit ID" },
      { key: "permit_number", label: "Permit Number" },
      { key: "tenant_id", label: "Tenant ID" },
      { key: "title", label: "Title" },
      { key: "status", label: "Status" },
      { key: "planned_start", label: "Planned Start" },
      { key: "planned_end", label: "Planned End" },
    ]

    availableFields.forEach((item) => {
      if (options.some((option) => option.key === item.fieldKey)) return
      options.push({
        key: item.fieldKey,
        label: item.label || item.fieldKey,
      })
    })

    return options
  }, [availableFields])

  const {
    fieldDialogOpen,
    setFieldDialogOpen,
    editingFieldIndex,
    fieldForm,
    setFieldForm,
    dragFieldIndex,
    dragOverFieldIndex,
    openAddField,
    openEditField,
    saveField,
    deleteField,
    handleFieldDragStart,
    handleFieldDragOver,
    handleFieldDrop,
    handleFieldDragEnd,
    filterDialogOpen,
    setFilterDialogOpen,
    editingFilterIndex,
    filterForm,
    setFilterForm,
    dragFilterIndex,
    dragOverFilterIndex,
    openAddFilter,
    openEditFilter,
    saveFilter,
    deleteFilter,
    handleFilterDragStart,
    handleFilterDragOver,
    handleFilterDrop,
    handleFilterDragEnd,
  } = useReportTemplateFieldFilterConfig({
    fields,
    setFields,
    filters,
    setFilters,
    fieldKeyOptions,
  })

  const headerSection = useMemo(
    () => layout.headerSection ?? { enabled: false, height: 120, widgets: [] as ReportLayoutWidgetDto[] },
    [layout.headerSection],
  )
  const footerSection = useMemo(
    () => layout.footerSection ?? { enabled: false, height: 80, widgets: [] as ReportLayoutWidgetDto[] },
    [layout.footerSection],
  )
  const reportDatasources = useMemo(
    () => getLayoutDatasources(layout),
    [layout],
  )

  const fullCanvasMetrics = useMemo(
    () => getCanvasMetrics(layout.orientation),
    [layout.orientation],
  )
  const manualPageCount = useMemo(
    () => getLayoutManualPageCount(layout),
    [layout],
  )
  const flowSections = useMemo(
    () => getLayoutFlowSections(layout),
    [layout],
  )
  const primaryFlowSectionId = flowSections[0]?.sectionId || DEFAULT_FLOW_SECTION_ID
  useEffect(() => {
    const hasActive = flowSections.some((section) => section.sectionId === activeBodyFlowSectionId)
    if (!hasActive) {
      setActiveBodyFlowSectionId(primaryFlowSectionId)
    }
  }, [activeBodyFlowSectionId, flowSections, primaryFlowSectionId])
  const {
    collapsedFlowSections,
    setSectionWidgets,
    moveWidgetBetweenSections,
    setHeaderSectionEnabled,
    setFooterSectionEnabled,
    setHeaderSectionHeight,
    setFooterSectionHeight,
    setManualPageCount,
    addFlowSection,
    updateFlowSection,
    moveFlowSection,
    removeFlowSection,
    toggleDesignFlowSectionCollapsed,
  } = useLayoutSectionManagement({
    layout,
    setLayout,
    layoutSection,
    setLayoutSection,
    flowSections,
    primaryFlowSectionId,
    selectedWidgetId,
    setSelectedWidgetId,
  })
  const headerEnabled = headerSection.enabled
  const footerEnabled = footerSection.enabled
  const headerHeight = headerEnabled ? clamp(Math.round(headerSection.height || 120), 60, 360) : 0
  const footerHeight = footerEnabled ? clamp(Math.round(footerSection.height || 80), 60, 360) : 0
  const baseBodyCanvasHeight = Math.max(240, fullCanvasMetrics.height - headerHeight - footerHeight)
  const editSectionOffsetY = useMemo(() => {
    if (layoutSection === "header") return 0
    if (layoutSection === "footer") return Math.max(0, fullCanvasMetrics.height - footerHeight)
    return headerHeight
  }, [footerHeight, fullCanvasMetrics.height, headerHeight, layoutSection])

  const bodyWidgetsForDesignPage = useMemo(
    () =>
      (layout.widgets ?? []).filter((widget) => {
        const widgetPage = clamp(getWidgetManualPage(widget, 1), 1, manualPageCount)
        return widgetPage === designBodyPage
      }),
    [designBodyPage, layout.widgets, manualPageCount],
  )

  const designFlowSectionLayout = useMemo(() => {
    const sectionGap = 0
    const collapsedHeight = 44
    const sectionLeftPadding = 12

    const sectionHeightMap = new Map<string, number>()
    const widgetRenderYMap: Record<string, number> = {}
    const boxes: DesignFlowSectionBox[] = []

    let cursorY = 0
    flowSections.forEach((section, index) => {
      const isCollapsed = Boolean(collapsedFlowSections[section.sectionId])
      const sectionMinHeight = clamp(Math.round(Number(section.minHeight) || 140), MIN_FLOW_SECTION_HEIGHT, 2400)
      const sectionMembers = bodyWidgetsForDesignPage.filter(
        (widget) => getWidgetFlowSectionId(widget, primaryFlowSectionId) === section.sectionId,
      )
      const baseHeight = isCollapsed ? collapsedHeight : sectionMinHeight

      sectionHeightMap.set(section.sectionId, baseHeight)
      boxes.push({
        sectionId: section.sectionId,
        name: section.name,
        breakMode: section.breakMode === "newPage" ? "newPage" : "flow",
        isCollapsed,
        minHeight: sectionMinHeight,
        x: sectionLeftPadding,
        y: cursorY,
        width: Math.max(160, fullCanvasMetrics.width - sectionLeftPadding * 2),
        height: baseHeight,
        widgetCount: sectionMembers.length,
      })

      if (!isCollapsed) {
        sectionMembers.forEach((widget) => {
          const localY = Math.max(0, toFiniteNumber(widget.y, 0))
          widgetRenderYMap[widget.id] = cursorY + localY
        })
      }

      cursorY += baseHeight
      if (index < flowSections.length - 1) {
        cursorY += sectionGap
      }
    })

    const contentExtentBottom = bodyWidgetsForDesignPage.reduce((maxBottom, widget) => {
      const widgetTop = widgetRenderYMap[widget.id] ?? 0
      const widgetHeight = Math.max(1, toFiniteNumber(widget.height, 1))
      return Math.max(maxBottom, widgetTop + widgetHeight)
    }, 0)
    const totalHeight = Math.max(
      baseBodyCanvasHeight,
      cursorY > 0 ? cursorY : baseBodyCanvasHeight,
      contentExtentBottom,
    )
    return { boxes, widgetRenderYMap, sectionHeightMap, totalHeight }
  }, [baseBodyCanvasHeight, bodyWidgetsForDesignPage, collapsedFlowSections, flowSections, fullCanvasMetrics.width, primaryFlowSectionId])

  const designFlowSectionBoxes = designFlowSectionLayout.boxes
  const designWidgetRenderYMap = designFlowSectionLayout.widgetRenderYMap
  const designFlowSectionHeightMap = designFlowSectionLayout.sectionHeightMap
  const designBodyCanvasHeight = designFlowSectionLayout.totalHeight

  const editCanvasMetrics = useMemo(() => {
    if (layoutSection === "header") {
      return { width: fullCanvasMetrics.width, height: Math.max(60, headerHeight || 120) }
    }
    if (layoutSection === "footer") {
      return { width: fullCanvasMetrics.width, height: Math.max(60, footerHeight || 80) }
    }
    return { width: fullCanvasMetrics.width, height: designBodyCanvasHeight }
  }, [designBodyCanvasHeight, footerHeight, fullCanvasMetrics.width, headerHeight, layoutSection])

  useEffect(() => {
    setDesignBodyPage((prev) => clamp(prev, DEFAULT_MANUAL_PAGE_COUNT, manualPageCount))
  }, [manualPageCount])

  const editableWidgets = useMemo(() => {
    if (layoutSection === "header") return headerSection.widgets ?? []
    if (layoutSection === "footer") return footerSection.widgets ?? []
    return bodyWidgetsForDesignPage
  }, [bodyWidgetsForDesignPage, footerSection.widgets, headerSection.widgets, layoutSection])

  const selectedWidget = useMemo(
    () => editableWidgets.find((widget) => widget.id === selectedWidgetId) ?? null,
    [editableWidgets, selectedWidgetId],
  )
  const selectedWidgetBorder = useMemo(
    () => (selectedWidget ? getWidgetBorderConfig(selectedWidget) : null),
    [selectedWidget],
  )
  const selectedWidgetCornerRadius = useMemo(
    () => (selectedWidget ? getWidgetCornerRadius(selectedWidget) : 0),
    [selectedWidget],
  )
  const selectedWidgetTextAlign = useMemo(
    () => (selectedWidget ? getWidgetTextAlign(selectedWidget) : "left"),
    [selectedWidget],
  )
  const selectedWidgetVerticalAlign = useMemo(
    () => (selectedWidget ? getWidgetVerticalAlign(selectedWidget) : "top"),
    [selectedWidget],
  )
  const selectedWidgetTypography = useMemo(
    () => (selectedWidget ? getWidgetTypographyConfig(selectedWidget) : { fontSize: 14, fontWeight: "normal" as FontWeightMode, fontStyle: "normal" as FontStyleMode }),
    [selectedWidget],
  )
  const tableSelectedColumns = useMemo(() => {
    if (!selectedWidget || selectedWidget.type !== "table") return []
    return String(selectedWidget.props?.columns || "")
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean)
  }, [selectedWidget])
  const selectedTableSource = useMemo<TableSourceMode>(() => {
    if (!selectedWidget || selectedWidget.type !== "table") return "reportRows"
    const raw = String(selectedWidget.props?.tableSource || "reportRows")
    if (raw === "workers") return "workers"
    if (raw === "datasource") return "datasource"
    return "reportRows"
  }, [selectedWidget])
  const selectedTableDatasourceId = useMemo(() => {
    if (!selectedWidget || selectedWidget.type !== "table") return ""
    return String(selectedWidget.props?.datasourceId || "")
  }, [selectedWidget])
  const selectedTableColumnWidths = useMemo<TableColumnWidthMap>(() => {
    if (!selectedWidget || selectedWidget.type !== "table") return {}
    return parseTableColumnWidthMap(selectedWidget.props?.columnWidths)
  }, [selectedWidget])
  const tableColumnOptions = useMemo(() => {
    if (!selectedWidget || selectedWidget.type !== "table") return []
    const datasource = reportDatasources.find((item) => item.id === selectedTableDatasourceId)
    const datasourceColumns = datasource?.fields?.map((item) => ({ key: item.fieldKey, label: item.label || item.fieldKey })) ?? []
    const base =
      selectedTableSource === "workers"
        ? WORKER_TABLE_COLUMNS
        : selectedTableSource === "datasource"
          ? datasourceColumns
          : availableFields.map((item) => ({ key: item.fieldKey, label: item.label || item.fieldKey }))
    return [{ key: TABLE_ROW_NO_KEY, label: "No." }, ...base]
  }, [availableFields, reportDatasources, selectedTableDatasourceId, selectedTableSource, selectedWidget])
  const sortedWidgets = useMemo(
    () => [...editableWidgets].sort((a, b) => (a.zIndex || 0) - (b.zIndex || 0)),
    [editableWidgets],
  )
  const fieldLabelMap = useMemo(() => {
    const map = new Map<string, string>()
    availableFields.forEach((item) => {
      map.set(item.fieldKey, item.label || item.fieldKey)
    })
    reportDatasources.forEach((datasource) => {
      ;(datasource.fields ?? []).forEach((field) => {
        map.set(`${datasource.id}:${field.fieldKey}`, field.label || field.fieldKey)
      })
    })
    return map
  }, [availableFields, reportDatasources])
  const hasWorkersTable = useMemo(
    () =>
      [...(layout.widgets ?? []), ...(headerSection.widgets ?? []), ...(footerSection.widgets ?? [])].some(
        (widget) =>
          widget.type === "table" && String(widget.props?.tableSource || "reportRows") === "workers",
      ),
    [footerSection.widgets, headerSection.widgets, layout.widgets],
  )

  const {
    previewRows,
    previewRowIndex,
    setPreviewRowIndex,
    isLoadingPreviewData,
    previewDataError,
    previewDataSource,
    previewElapsedMs,
    previewWorkers,
    isLoadingPreviewWorkers,
    previewWorkersError,
    selectedPreviewRow,
    loadLayoutPreviewData,
  } = useReportTemplatePreviewRuntime({
    template,
    fields: availableFields,
    filters,
    layoutViewMode,
    hasWorkersTable,
    defaultTenantId: activeTenantId,
  })

  const previewValueMap = useMemo(() => {
    const map = new Map<string, string>()
    availableFields.forEach((item) => {
      const key = item.fieldKey
      const rowValue =
        selectedPreviewRow?.[key] ??
        (key === "permit_number" ? selectedPreviewRow?.__permitNumber : undefined) ??
        (key === "permit_id" ? selectedPreviewRow?.__permitId : undefined)
      if (rowValue !== undefined && rowValue !== null && String(rowValue).trim() !== "") {
        map.set(key, formatPreviewValue(rowValue))
      } else {
        map.set(key, item.label || item.fieldKey)
      }
    })
    return map
  }, [availableFields, selectedPreviewRow])

  const handleTestDatasource = useCallback(async (datasourceId: string) => {
    const datasource = reportDatasources.find((item) => item.id === datasourceId)
    if (!datasource) return
    if (!datasource.objectName.trim()) return

    setLoadingDatasourceId(datasourceId)
    try {
      const context = buildReportDatasourceContext(selectedPreviewRow, activeTenantId)
      const result = await previewReportDatasource({
        tenantId: activeTenantId,
        sourceType: datasource.sourceType,
        objectName: datasource.objectName,
        cardinality: datasource.cardinality,
        bindings: datasource.bindings ?? [],
        context,
        rowLimit: datasource.rowLimit ?? 20,
      })

      setDatasourcePreviewResults((prev) => ({ ...prev, [datasourceId]: result }))
      if (result.columns.length > 0) {
        setLayout((prev) => ({
          ...prev,
          datasources: (prev.datasources ?? []).map((item) => {
            if (item.id !== datasourceId) return item
            const currentSignature = JSON.stringify(item.fields ?? [])
            const nextSignature = JSON.stringify(result.columns)
            return currentSignature === nextSignature ? item : { ...item, fields: result.columns }
          }),
        }))
      }
    } catch (datasourceError) {
      console.error(datasourceError)
    } finally {
      setLoadingDatasourceId((current) => (current === datasourceId ? null : current))
    }
  }, [activeTenantId, reportDatasources, selectedPreviewRow, setLayout])

  useEffect(() => {
    if (layoutViewMode !== "preview") return
    const activeSources = reportDatasources.filter((item) => item.objectName.trim())
    if (activeSources.length === 0) {
      setDatasourcePreviewResults({})
      return
    }

    let cancelled = false

    const run = async () => {
      const context = buildReportDatasourceContext(selectedPreviewRow, activeTenantId)
      const nextEntries = await Promise.all(
        activeSources.map(async (datasource) => {
          const result = await previewReportDatasource({
            tenantId: activeTenantId,
            sourceType: datasource.sourceType,
            objectName: datasource.objectName,
            cardinality: datasource.cardinality,
            bindings: datasource.bindings ?? [],
            context,
            rowLimit: datasource.rowLimit ?? 20,
          })
          return [datasource.id, result] as const
        }),
      )

      if (cancelled) return

      const nextMap = Object.fromEntries(nextEntries)
      setDatasourcePreviewResults(nextMap)
      setLayout((prev) => {
        let changed = false
        const nextDatasources = (prev.datasources ?? []).map((datasource) => {
          const nextFields = nextMap[datasource.id]?.columns?.length
            ? nextMap[datasource.id].columns
            : datasource.fields ?? []
          const currentSignature = JSON.stringify(datasource.fields ?? [])
          const nextSignature = JSON.stringify(nextFields)
          if (currentSignature === nextSignature) return datasource
          changed = true
          return {
            ...datasource,
            fields: nextFields,
          }
        })

        return changed ? { ...prev, datasources: nextDatasources } : prev
      })
    }

    run().catch((datasourceError) => {
      if (!cancelled) {
        console.error(datasourceError)
      }
    })

    return () => {
      cancelled = true
    }
  }, [activeTenantId, layoutViewMode, reportDatasources, selectedPreviewRow, setLayout])

  const {
    movingWidgetId,
    movingSectionId,
    resizingSectionId,
    resizingWidgetId,
    setMovingWidgetId,
    handleResizeMouseDown,
    handleWidgetMouseDown,
    handleFlowSectionMouseDown,
    handleFlowSectionResizeMouseDown,
  } = useLayoutWidgetInteractions({
    canvasRef,
    editCanvasMetrics,
    layoutViewMode,
    layoutSection,
    layoutWidgets: layout.widgets ?? [],
    designBodyPage,
    manualPageCount,
    primaryFlowSectionId,
    flowSections,
    designFlowSectionBoxes,
    snapToGrid,
    gridSize,
    setSectionWidgets,
    setSelectedWidgetId,
    updateFlowSection,
  })

  const {
    updateWidget,
    updateWidgetProps,
    removeWidget,
    duplicateWidget,
    bringWidgetToFront,
    createWidgetAtPosition,
    handleWidgetTransferDragStart,
    handleCanvasDragOver,
    handleWidgetTemplateDragStart,
    handleCanvasDrop,
    toggleTableColumn,
    moveTableColumn,
    setTableColumnWidth,
    setWidgetBorderSide,
    setWidgetBorderAll,
  } = useLayoutWidgetOperations({
    canvasRef,
    layoutSection,
    editCanvasMetrics,
    primaryFlowSectionId,
    manualPageCount,
    designBodyPage,
    designFlowSectionHeightMap,
    designFlowSectionBoxes,
    snapToGrid,
    gridSize,
    setSectionWidgets,
    moveWidgetBetweenSections,
    setSelectedWidgetId,
    setMovingWidgetId,
    selectedWidget,
  })

  const {
    getFieldDisplay,
    getTableColumnLabel,
    getTableColumnWidths,
    previewHeaderWidgets,
    previewFooterWidgets,
    previewBodyRenderEntries,
    previewTotalPages,
  } = useLayoutPreviewComposition({
    datasources: reportDatasources,
    datasourcePreviewResults,
    headerWidgets: headerSection.widgets ?? [],
    bodyWidgets: layout.widgets ?? [],
    footerWidgets: footerSection.widgets ?? [],
    baseBodyCanvasHeight,
    flowSections,
    manualPageCount,
    primaryFlowSectionId,
    previewRows,
    previewWorkers,
    fieldLabelMap,
    previewValueMap,
    previewPageIndex,
    setPreviewPageIndex,
  })

  const validationIssues = useMemo(
    () => validateReportDesigner({ layout, availableFields }),
    [availableFields, layout],
  )

  const handleActiveBodyFlowSectionChange = useCallback((sectionId: string) => {
    setLayoutSection("body")
    setActiveBodyFlowSectionId(sectionId)
    setSelectedWidgetId(null)
  }, [])

  const handleWidgetSelection = useCallback((widgetId: string) => {
    setSelectedWidgetId(widgetId)
    if (layoutSection !== "body") return
    const widget = (layout.widgets ?? []).find((item) => item.id === widgetId)
    if (!widget) return
    setActiveBodyFlowSectionId(getWidgetFlowSectionId(widget, primaryFlowSectionId))
  }, [layout.widgets, layoutSection, primaryFlowSectionId])

  useEffect(() => {
    if (layoutViewMode !== "design") return
    if (!selectedWidget) return

    const handler = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null
      const tagName = target?.tagName?.toLowerCase()
      const isEditableTarget =
        Boolean(target?.isContentEditable) ||
        tagName === "input" ||
        tagName === "textarea" ||
        tagName === "select"

      if (isEditableTarget) return

      if ((event.ctrlKey || event.metaKey) && event.key.toLowerCase() === "d") {
        event.preventDefault()
        duplicateWidget(selectedWidget.id)
        return
      }

      if (event.key === "Delete" || event.key === "Backspace") {
        event.preventDefault()
        removeWidget(selectedWidget.id)
      }
    }

    window.addEventListener("keydown", handler)
    return () => window.removeEventListener("keydown", handler)
  }, [duplicateWidget, layoutViewMode, removeWidget, selectedWidget])

  const {
    isSaving,
    uploadingImageWidgetId,
    handleImageFileSelected,
    handleSaveDefinition,
  } = useReportTemplateEditorActions({
    reportTemplateId,
    template,
    fields,
    filters,
    layout,
    defaultTenantId: activeTenantId,
    setTemplate,
    setFields,
    setFilters,
    setLayout,
    setSelectedWidgetId,
    updateWidgetProps: (widgetId, patch) => updateWidgetProps(widgetId, patch),
  })

  return (
    <div className="space-y-6">
      <EditorHeaderCard
        onBack={onBack}
        onRun={onRun && reportTemplateId ? () => onRun(reportTemplateId, activeTenantId) : undefined}
        onSave={handleSaveDefinition}
        isSaving={isSaving}
        saveDisabled={isSaving || isLoading || !template}
      />

      {error ? <EditorErrorAlert message={error} /> : null}

      {isLoading ? <EditorLoadingCard /> : null}

      {!isLoading && !template ? <EditorNotFoundCard /> : null}

      {!isLoading && template ? (
        <>
          <TemplateSummaryCard template={template} />

          <ReportTemplateDefinitionTabs
            datasourcesCount={reportDatasources.length}
            fieldsCount={fields.length}
            filtersCount={filters.length}
            layoutWidgetCount={(layout.widgets?.length ?? 0) + (layout.headerSection?.widgets?.length ?? 0) + (layout.footerSection?.widgets?.length ?? 0)}
            datasourcesTabProps={{
              datasources: reportDatasources,
              validationIssues,
              viewOptions: datasourceViewOptions,
              contextKeyOptions: datasourceContextKeyOptions,
              previewResults: datasourcePreviewResults,
              loadingDatasourceId,
              isRefreshingViews: isRefreshingDatasourceViews,
              onRefreshViews: () => void loadDatasourceViews(),
              onTestDatasource: (datasourceId) => void handleTestDatasource(datasourceId),
              onChange: (datasources) => setLayout((prev) => ({ ...prev, datasources })),
            }}
            fieldsTabProps={{
              fields,
              dragFieldIndex,
              dragOverFieldIndex,
              onAddField: openAddField,
              onEditField: openEditField,
              onDeleteField: deleteField,
              onFieldDragStart: handleFieldDragStart,
              onFieldDragOver: handleFieldDragOver,
              onFieldDrop: handleFieldDrop,
              onFieldDragEnd: handleFieldDragEnd,
            }}
            filtersTabProps={{
              filters,
              dragFilterIndex,
              dragOverFilterIndex,
              onAddFilter: openAddFilter,
              onEditFilter: openEditFilter,
              onDeleteFilter: deleteFilter,
              onFilterDragStart: handleFilterDragStart,
              onFilterDragOver: handleFilterDragOver,
              onFilterDrop: handleFilterDrop,
              onFilterDragEnd: handleFilterDragEnd,
            }}
            layoutTabProps={{
              layoutViewMode,
              onLayoutViewModeChange: setLayoutViewMode,
              validationIssues,
              previewToolbarProps: {
                isLoadingPreviewData,
                hasTemplate: Boolean(template),
                onReload: () => loadLayoutPreviewData().catch(() => undefined),
                previewRows,
                previewRowIndex,
                onPreviewRowIndexChange: setPreviewRowIndex,
                previewDataSource,
                previewElapsedMs,
                hasWorkersTable,
                previewWorkersCount: previewWorkers.length,
                previewDataError,
                previewWorkersError,
                isLoadingPreviewWorkers,
              },
              workspaceProps: {
                layout,
                layoutViewMode,
                designSection: layoutSection,
                activeBodyFlowSectionId,
                designBodyPage,
                manualPageCount,
                flowSections,
                designWidgetRenderYMap,
                designFlowSectionBoxes,
                bodyWidgetsForDesignPage,
                designSectionOffsetY: editSectionOffsetY,
                fullCanvasMetrics,
                editCanvasMetrics,
                headerEnabled,
                footerEnabled,
                headerHeight,
                footerHeight,
                previewPageIndex,
                previewTotalPages,
                canvasRef,
                sortedWidgets,
                previewBodyRenderEntries,
                previewHeaderWidgets,
                previewFooterWidgets,
                selectedWidgetId,
                movingWidgetId,
                movingSectionId,
                resizingSectionId,
                resizingWidgetId,
                fieldLabelMap,
                getFieldDisplay,
                getTableColumnLabel,
                getTableColumnWidths,
                onTemplateDragStart: handleWidgetTemplateDragStart,
                onCreateWidgetAt: createWidgetAtPosition,
                onDesignSectionChange: setLayoutSection,
                onDesignBodyPageChange: setDesignBodyPage,
                onManualPageCountChange: setManualPageCount,
                onAddFlowSection: addFlowSection,
                onUpdateFlowSection: updateFlowSection,
                onMoveFlowSection: moveFlowSection,
                onRemoveFlowSection: removeFlowSection,
                onPageSizeChange: (value) => setLayout((prev) => ({ ...prev, pageSize: value })),
                onOrientationChange: (value) => {
                  setLayout((prev) => normalizeLayoutForEditor({ ...prev, orientation: value }))
                  setSelectedWidgetId(null)
                },
                onHeaderEnabledChange: setHeaderSectionEnabled,
                onFooterEnabledChange: setFooterSectionEnabled,
                onHeaderHeightChange: setHeaderSectionHeight,
                onFooterHeightChange: setFooterSectionHeight,
                onFrameChange: (patch) => {
                  setLayout((prev) => ({
                    ...prev,
                    frame: normalizeLayoutFrame({ ...(prev.frame ?? {}), ...patch }),
                  }))
                },
                snapToGrid,
                gridSize,
                onSnapToGridChange: setSnapToGrid,
                onGridSizeChange: setGridSize,
                onPreviewPageChange: setPreviewPageIndex,
                onCanvasDrop: handleCanvasDrop,
                onCanvasDragOver: handleCanvasDragOver,
                onRemoveWidget: removeWidget,
                onDuplicateWidget: duplicateWidget,
                onWidgetTransferDragStart: handleWidgetTransferDragStart,
                onActiveBodyFlowSectionChange: handleActiveBodyFlowSectionChange,
                onCanvasClick: () => setSelectedWidgetId(null),
                onWidgetMouseDown: handleWidgetMouseDown,
                onFlowSectionMouseDown: handleFlowSectionMouseDown,
                onFlowSectionResizeMouseDown: handleFlowSectionResizeMouseDown,
                onToggleFlowSectionCollapsed: toggleDesignFlowSectionCollapsed,
                onWidgetClick: handleWidgetSelection,
                onResizeMouseDown: handleResizeMouseDown,
              },
              widgetPropertiesProps: {
                layoutSection: layoutSection,
                manualPageCount,
                flowSections,
                selectedWidget,
                canvasMetrics: editCanvasMetrics,
                selectedWidgetBorder,
                selectedWidgetCornerRadius,
                selectedWidgetTextAlign,
                selectedWidgetVerticalAlign,
                selectedWidgetTypography,
                fieldOptions,
                datasources: reportDatasources,
                fieldLabelMap,
                tableSource: selectedTableSource,
                tableDatasourceId: selectedTableDatasourceId,
                tableColumnOptions,
                tableSelectedColumns,
                tableColumnWidths: selectedTableColumnWidths,
                uploadingImageWidgetId,
                imageFileInputRef,
                updateWidget,
                updateWidgetProps,
                setWidgetBorderSide,
                setWidgetBorderAll,
                toggleTableColumn,
                moveTableColumn,
                setTableColumnWidth,
                handleImageFileSelected,
                bringWidgetToFront,
                duplicateWidget,
                removeWidget,
              },
            }}
          />
        </>
      ) : null}

      {!isLoading && template ? <FloatingSaveButton onSave={handleSaveDefinition} isSaving={isSaving} /> : null}

      <FieldDialog
        open={fieldDialogOpen}
        onOpenChange={setFieldDialogOpen}
        editingFieldIndex={editingFieldIndex}
        fieldForm={fieldForm}
        setFieldForm={setFieldForm}
        onApply={saveField}
      />

      <FilterDialog
        open={filterDialogOpen}
        onOpenChange={setFilterDialogOpen}
        editingFilterIndex={editingFilterIndex}
        filterForm={filterForm}
        setFilterForm={setFilterForm}
        fieldKeyOptions={fieldKeyOptions}
        onApply={saveFilter}
      />
    </div>
  )
}


